import Brain from 'assets/icons/Brain';
import Chat from 'assets/icons/Chat';
import Clock from 'assets/icons/Clock';
import Dash from 'assets/icons/Dash';
import Filter from 'assets/icons/Filter';
import Grid from 'assets/icons/Grid';
import List from 'assets/icons/List';
import Money from 'assets/icons/Money';
import People from 'assets/icons/People';
import Refresh from 'assets/icons/Refresh';
import Button from 'common/components/Button';
import Chip from 'common/components/Chip';
import ChipDropdown from 'common/components/ChipDropdown';
import Divider from 'common/components/Divider';
import Drawer from 'common/components/Drawer';
import InlineLoader from 'common/components/InlineLoader';
import { SearchInput } from 'common/components/SearchInput';
import Tooltip from 'common/components/Tooltip';
import useNavigate from 'common/hooks/useNavigate';
import { useSearchTerm } from 'common/hooks/useSearchTerm';
import { useToast } from 'common/hooks/useToast';
import useTranslation from 'common/hooks/useTranslation';
import {
  selectCurrentCompany,
  selectCurrentToken,
  selectUser,
} from 'common/slices/auth.slice';
import {
  useGetOfferAnalyticsBySlugQuery,
  useGetOfferBySlugQuery,
  useUpdateOfferStatusMutation,
} from 'common/slices/offerApi.slice';
import { cn, formatDate, isSyncableSource } from 'common/utils';
import { DeepChat } from 'deep-chat-react';
import { isEmpty } from 'lodash';
import OfferSwitch from 'offers/components/OfferSwitch';
import OfferSyncStatusBadge from 'offers/components/OfferSyncStatusBadge';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ShowFeature } from 'split';
import { useOnClickOutside } from 'usehooks-ts';

import { DEFAULT_FILTER, SUBMISSIONS_FILTERS } from './common/filters.config';
import AutoSyncSwitch from './components/AutoSyncSwitch';
import { DescriptionTab } from './components/DescriptionTab';
import { InformationTab } from './components/InformationTab';
import OfferAnalysis from './components/OfferAnalysis';
import OfferDetailsSkeleton from './components/OfferDetailsSkeleton';
import OptionsMenu from './components/OptionsMenu';
import { QuestionsTab } from './components/QuestionsTab';
import { StatusCombo } from './components/StatusCombo';
import { TestsTab } from './components/TestsTab';
import CandidateDrawer from './components/candidate-drawer';
import KanbanBoard from './components/kanban-board';
import DataSourceModal from './components/modals/DataSourceModal';
import DeleteModal from './components/modals/DeleteModal';
import FiltersModal from './components/modals/FiltersModal';
import GenerateAIModal from './components/modals/GenerateAIModal';
import HiredModal from './components/modals/HiredModal';
import ImportModal from './components/modals/ImportModal';
import SyncModal from './components/modals/SyncModal';
import QuestionsDrawer from './components/questions-drawer';
import { useAssistantChat } from './hooks/useAssistantChat';
import { useAutoSyncSwitch } from './hooks/useAutoSyncSwitch';
import { useColumnRefetch } from './hooks/useColumnRefetch';

const OfferDetails = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { offerSlug, submissionSlug: submissionSlugParam } = useParams();
  const { t, i18n } = useTranslation('offers\\details');

  const chatRef = useRef(null);
  const chatButtonRef = useRef(null);

  const token = useSelector(selectCurrentToken);
  const currentCompany = useSelector(selectCurrentCompany);
  const currentUser = useSelector(selectUser);

  const hasAccessToForceUpdate =
    currentUser.role.name === 'staff' || currentUser.role.name === 'admin';

  const hasAccessToSetupAutoSync =
    currentUser.role.name === 'staff' || currentUser.role.name === 'admin';

  const {
    data: offer,
    isFetching,
    error: offerError,
  } = useGetOfferBySlugQuery({ offerSlug });

  const { data: analytics, isLoading: isLoadingAnalytics } =
    useGetOfferAnalyticsBySlugQuery({ offerSlug });

  const [updateOfferStatus, { isLoading: isUpdatingOfferStatus }] =
    useUpdateOfferStatusMutation();

  const [isCardCompact, setCardCompact] = useState(true);
  const [currentTab, setCurrentTab] = useState('board');
  const [isCandidateDrawerOpen, setCandidateDrawerOpen] = useState(false);
  const [isQuestionsDrawerOpen, setQuestionsDrawerOpen] = useState(false);
  const [isImportOpen, setImportOpen] = useState(false);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [isHiredOpen, setHiredOpen] = useState(false);
  const [isSyncOpen, setSyncOpen] = useState(false);
  const [isForceUpdate, setForceUpdate] = useState(false);
  const [isFiltersOpen, setFiltersOpen] = useState(false);
  const [isSetupDataSourceOpen, setSetupDataSourceOpen] = useState(false);
  const [isManualSyncStarted, setSyncing] = useState(false);
  const [isChatOpen, setChatOpen] = useState(false);
  const [isResettingSubmissions, setResettingSubmissions] = useState(false);
  const [submissionSlug, setSubmissionSlug] = useState(submissionSlugParam);
  const [currentSubmissionSlug, setCurrentSubmissionSlug] =
    useState(submissionSlugParam);
  const [questions, setQuestions] = useState(null);
  const [initialValues, setInitialValues] = useState({ questions: [] });
  const [isFavoriteParam, setIsFavoriteParam] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [submissionsFilter, setSubmissionsFilter] = useState(
    SUBMISSIONS_FILTERS[DEFAULT_FILTER],
  );
  const [isGenerateAIModalOpen, setGenerateAIModalOpen] = useState(false);

  const isLoading = isFetching || isLoadingAnalytics;

  useOnClickOutside(chatRef, (event) => {
    if (
      chatButtonRef.current &&
      !chatButtonRef.current.contains(event.target)
    ) {
      setChatOpen(false);
    }
  });

  const openProfileDrawer = (slug) => {
    setCurrentSubmissionSlug(slug);
    setCandidateDrawerOpen(true);
  };

  const closeProfileDrawer = () => {
    setSubmissionSlug(null);
    setCandidateDrawerOpen(false);
    navigate(`/offers/${offer.slug}`);
  };

  useEffect(() => {
    if (submissionSlugParam) {
      openProfileDrawer(submissionSlugParam);
    }
  }, [submissionSlugParam]);

  const toggleChat = () => {
    setChatOpen(!isChatOpen);
  };

  const chatRequestInterceptor = (requestDetails) => {
    requestDetails.body = {
      question: requestDetails.body.messages[0].text,
      n_messages: 1,
    };
    return requestDetails;
  };

  const chatResponseInterceptor = (response) => {
    const lastMessage = response.messages.pop();
    return { text: lastMessage.value };
  };

  const handleUpdateOfferStatus = async (nextState) => {
    try {
      await updateOfferStatus({ offerSlug, offerStatus: nextState }).unwrap();
      toast.newToast('positive', t('toasts.success-status'));
    } catch (err) {
      if (err?.status === 403) {
        toast.newToast('negative', t('toasts.forbidden'));
      } else {
        toast.newToast('negative', t('toasts.something-wrong'));
      }
    }
  };

  const handleImport = () => setImportOpen(true);
  const handleSetupDataSource = () => setSetupDataSourceOpen(true);
  const handleUpdate = () => navigate(`/offers/${offer.slug}/update`);
  const handleDelete = () => setDeleteOpen(true);
  const handleSync = () => setSyncOpen(true);

  const handleHired = (subSlug) => {
    setSubmissionSlug(subSlug);
    setHiredOpen(true);
  };

  const handleCardFormat = () => setCardCompact((prev) => !prev);

  const handleSortFilterKeyChange = (sortFilterKey) => {
    const sortFilter = SUBMISSIONS_FILTERS.find(
      (filter) => filter.key === sortFilterKey,
    );

    sortFilter ? setSubmissionsFilter(sortFilter) : setSubmissionsFilter(null);
  };

  const handleCloseFilters = (hasChanges) => {
    if (hasChanges) {
      setFiltersOpen(true);
    } else {
      setQuestionsDrawerOpen(false);
    }
  };

  const externalSource = offer?.external_source;

  const {
    isActive: isAutoSyncActive,
    setIsActive: setAutoSyncActive,
    isLoading: isAutoSyncChanging,
  } = useAutoSyncSwitch(externalSource?.is_periodic_sync, offerSlug);

  const { searchTerm, debouncedSearchTerm, handleSearchTermChange } =
    useSearchTerm();

  const { refreshColumn } = useColumnRefetch(
    offerSlug,
    debouncedSearchTerm,
    isFavoriteParam,
    isCardCompact,
    questions,
    submissionsFilter?.value,
  );

  const {
    chatStatus,
    isChatReady,
    isChatBusy,
    isChatSyncing,
    chatInitialMessages,
    refetchChat,
    handleResetAssistant,
  } = useAssistantChat({ offerSlug });

  if (offerError) {
    const offerErrorMessage = offerError.data?.errors[0]?.detail;
    toast.newToast('negative', offerErrorMessage);
    navigate('/dashboard');
    return null;
  }

  if (!offer) {
    return null;
  }

  return (
    <div className='flex flex-col h-full w-full max-h-full max-w-full min-w-[1280px] px-6 pt-6 relative'>
      {isLoading ? (
        <OfferDetailsSkeleton />
      ) : (
        <div className='flex flex-col'>
          <ShowFeature featureFlag='WEB_NO_ACTION_UI'>
            {offer?.expires_at && (
              <div className='flex items-center gap-4 mb-2 h-[28px]'>
                {offer?.urgent && (
                  <Chip variant='primary'>
                    <Clock size='12px' color='#F16347' />
                    Closing soon
                  </Chip>
                )}
                <p className='text-xs text-sonic-silver'>
                  {offer?.expires_at
                    ? formatDate(offer?.expires_at, i18n)
                    : t('no-deadline')}
                </p>
              </div>
            )}
          </ShowFeature>

          <div className='flex items-center justify-between mb-4'>
            <h1 className='text-3xl text-dark-liver font-semibold'>
              {offer?.name}
            </h1>
            <div className='flex items-center gap-6'>
              {offer?.salary_min && offer?.salary_max && (
                <div className='flex items-center gap-1.5'>
                  <Money size='20px' color='#7A7A7A' />
                  <p className='text-sm text-sonic-silver'>
                    {`${offer?.salary_min}$ - ${offer?.salary_max}$`}
                  </p>
                </div>
              )}
              <div className='flex items-center gap-1.5'>
                <People size='20px' color='#7A7A7A' />
                <p className='text-sm text-sonic-silver'>
                  {offer?.n_vacancies}
                </p>
              </div>
              <OfferSyncStatusBadge
                externalSource={externalSource}
                activeIntegration={currentCompany?.active_integration}
                isManualSyncStarted={isManualSyncStarted}
                i18n={i18n}
                t={t}
              />
            </div>
          </div>

          <ShowFeature featureFlag='SHOW_OFFER_ANALYSIS'>
            <OfferAnalysis analytics={analytics} />
          </ShowFeature>

          <div className='flex items-center justify-between mb-2 z-10'>
            <ul className='flex items-center gap-4'>
              <li
                className={cn(
                  'font-semibold select-none text-sonic-silver cursor-pointer',
                  {
                    'text-vibrant-orange decoration-2 underline underline-offset-[20px]':
                      currentTab === 'board',
                  },
                )}
                onClick={() => setCurrentTab('board')}
              >
                {t('board')}
              </li>
              <li
                className={cn(
                  'font-semibold select-none text-sonic-silver cursor-pointer',
                  {
                    'text-vibrant-orange decoration-2 underline underline-offset-[20px]':
                      currentTab === 'information',
                  },
                )}
                onClick={() => setCurrentTab('information')}
              >
                {t('information')}
              </li>
              <li
                className={cn(
                  'font-semibold select-none text-sonic-silver cursor-pointer',
                  {
                    'text-vibrant-orange decoration-2 underline underline-offset-[20px]':
                      currentTab === 'description',
                  },
                )}
                onClick={() => setCurrentTab('description')}
              >
                {t('description')}
              </li>
              <li
                className={cn(
                  'font-semibold select-none text-sonic-silver cursor-pointer',
                  {
                    'text-vibrant-orange decoration-2 underline underline-offset-[20px]':
                      currentTab === 'tests',
                  },
                )}
                onClick={() => setCurrentTab('tests')}
              >
                {t('tests')}
              </li>
              <li
                className={cn(
                  'font-semibold select-none text-sonic-silver cursor-pointer',
                  {
                    'text-vibrant-orange decoration-2 underline underline-offset-[20px]':
                      currentTab === 'questions',
                  },
                )}
                onClick={() => setCurrentTab('questions')}
              >
                {t('questions')}
              </li>
            </ul>

            <div className='flex items-center gap-4 ml-4'>
              <ShowFeature featureFlag='SHOW_AUTO_SYNC_TOGGLE'>
                {isSyncableSource(currentCompany?.active_integration) &&
                  hasAccessToSetupAutoSync && (
                    <AutoSyncSwitch
                      isAutoSyncActive={isAutoSyncActive}
                      setAutoSyncActive={setAutoSyncActive}
                      isAutoSyncChanging={isAutoSyncChanging}
                      isAutoSyncDisabled={
                        externalSource?.external_offer_id === null ||
                        isAutoSyncChanging
                      }
                    />
                  )}
              </ShowFeature>
              <OfferSwitch setIsFavoriteParam={setIsFavoriteParam} />
              <SearchInput
                height='h-[38px]'
                placeholderText={t('search-placeholder')}
                searchTerm={searchTerm}
                handleChange={handleSearchTermChange}
              />
              <div className='relative'>
                <Button
                  size='icon'
                  variant='secondary'
                  onClick={() => setQuestionsDrawerOpen(true)}
                  disabled={isEmpty(offer?.questions)}
                >
                  <Filter size='16px' color='#7A7A7A' />
                </Button>
                {questions && questions?.questions.length > 0 && (
                  <div
                    className={cn(
                      'absolute inline-flex items-center justify-center',
                      'w-5 h-5 text-xs font-semibold text-white bg-vibrant-orange',
                      'rounded-full -top-2 -end-2',
                    )}
                  >
                    {questions.questions.length}
                  </div>
                )}
              </div>
              <Button
                size='icon'
                variant='secondary'
                onClick={handleCardFormat}
              >
                {isCardCompact ? (
                  <Grid size='16px' color='#7A7A7A' />
                ) : (
                  <List size='16px' color='#7A7A7A' />
                )}
              </Button>
              <ChipDropdown
                currentOption={submissionsFilter}
                options={SUBMISSIONS_FILTERS}
                onChange={(sortFilterKey) =>
                  handleSortFilterKeyChange(sortFilterKey)
                }
                contextTranslationKey={'offers\\details'}
                chipVariant='dropdown-outlined-light'
                chipClassName='h-[38px] min-w-fit truncate'
              />
              <StatusCombo
                currentStatus={offer?.status}
                handleUpdateOfferStatus={handleUpdateOfferStatus}
                isUpdatingOfferStatus={isUpdatingOfferStatus}
                chipClassName='h-[38px]'
              />
              <OptionsMenu
                handleImport={handleImport}
                handleSetupDataSource={handleSetupDataSource}
                handleUpdate={handleUpdate}
                handleDelete={handleDelete}
                handleSync={handleSync}
                handleResetAssistant={handleResetAssistant}
                handleResetSubmissionsAIInfo={() =>
                  setGenerateAIModalOpen(true)
                }
                isChatEnabled={isChatReady}
                isChatSyncing={isChatBusy}
                isResettingSubmissions={isResettingSubmissions}
                offer={offer}
                company={currentCompany}
              />
            </div>
          </div>
          <Divider />
        </div>
      )}

      {currentTab === 'board' && (
        <KanbanBoard
          offer={offer}
          itemsCompact={isCardCompact}
          handleHired={handleHired}
          searchTerm={debouncedSearchTerm}
          questions={questions}
          isFavoriteParam={isFavoriteParam}
          skipTests={offer.skip_tests}
          setCandidates={setCandidates}
          submissionsFilter={submissionsFilter?.value}
        />
      )}
      {currentTab === 'information' && <InformationTab offer={offer} />}
      {currentTab === 'description' && (
        <DescriptionTab offerDescription={offer.description} />
      )}
      {currentTab === 'tests' && <TestsTab tests={offer.tests} />}
      {currentTab === 'questions' && (
        <QuestionsTab questions={offer.questions_suggestions} />
      )}

      {/* Modals */}
      <ImportModal
        offerId={offer?.id}
        externalSourceId={offer?.external_source_code}
        isOpen={isImportOpen}
        setIsOpen={setImportOpen}
        refetchChatFn={refetchChat}
      />
      <DeleteModal
        offerId={offer?.id}
        isOpen={isDeleteOpen}
        setIsOpen={setDeleteOpen}
      />
      <SyncModal
        offerSlug={offer?.slug}
        isOpen={isSyncOpen}
        setIsOpen={setSyncOpen}
        setSyncing={setSyncing}
        setForceUpdate={setForceUpdate}
        isForceUpdate={isForceUpdate}
        hasAccessToForceUpdate={hasAccessToForceUpdate}
      />
      <FiltersModal
        isOpen={isFiltersOpen}
        setIsOpen={setFiltersOpen}
        closeQuestionsDrawer={() => setQuestionsDrawerOpen(false)}
      />
      <DataSourceModal
        offer={offer}
        isOpen={isSetupDataSourceOpen}
        setIsOpen={setSetupDataSourceOpen}
        offerSlug={offerSlug}
        refetchChatFn={refetchChat}
        externalOfferId={offer?.external_source?.external_offer_id}
      />
      {submissionSlug && (
        <HiredModal
          submissionSlug={submissionSlug}
          isOpen={isHiredOpen}
          setIsOpen={setHiredOpen}
        />
      )}
      <GenerateAIModal
        offerSlug={offerSlug}
        isOpen={isGenerateAIModalOpen}
        setIsOpen={setGenerateAIModalOpen}
        setResettingSubmissions={setResettingSubmissions}
      />

      <Drawer
        size='small'
        isOpen={isQuestionsDrawerOpen}
        setIsOpen={setQuestionsDrawerOpen}
        outsideClickable={false}
      >
        {isQuestionsDrawerOpen && (
          <QuestionsDrawer
            isOpen={isQuestionsDrawerOpen}
            closeDrawer={handleCloseFilters}
            questions={offer?.questions}
            setQuestions={setQuestions}
            initialValues={initialValues}
            setInitialValues={setInitialValues}
          />
        )}
      </Drawer>

      <Drawer
        size='large'
        isOpen={isCandidateDrawerOpen}
        setIsOpen={setCandidateDrawerOpen}
        handleClose={closeProfileDrawer}
      >
        {currentSubmissionSlug && (
          <CandidateDrawer
            offer={offer}
            submissionSlug={currentSubmissionSlug}
            isOpen={isCandidateDrawerOpen}
            closeDrawer={closeProfileDrawer}
            candidates={candidates}
            setCurrentSubmissionSlug={setCurrentSubmissionSlug}
            refreshColumn={refreshColumn}
          />
        )}
      </Drawer>

      {isChatReady && isChatOpen && (
        <div ref={chatRef} className='fixed bottom-24 right-4 z-10'>
          <header className='flex px-4 py-2 items-center justify-between bg-vibrant-orange border-t border-x border-[#f5f7fb0b] rounded-t-[10px]'>
            <div className='flex items-center gap-2'>
              <Brain color='white' />
              <span className='text-white'>{t('chat.header')}</span>
            </div>
            <div className='flex items-center gap-2'>
              <Tooltip
                alignment='start'
                content={<span>{t('chat.tooltip.toggle')}</span>}
              >
                <button className='flex' onClick={toggleChat}>
                  <Dash />
                </button>
              </Tooltip>

              <Tooltip
                alignment='start'
                content={<span>{t('chat.tooltip.reset')}</span>}
              >
                <button className='flex' onClick={handleResetAssistant}>
                  <div className='relative'>
                    <Refresh size={24} color='white' />
                  </div>
                </button>
              </Tooltip>
            </div>
          </header>
          <DeepChat
            initialMessages={chatInitialMessages}
            request={{
              url: `${process.env.REACT_APP_API_URL}/offers/${offerSlug}/chat`,
              headers: {
                Authorization: `Bearer ${token}`,
                'X-Company-Id': currentCompany.id,
              },
            }}
            requestInterceptor={chatRequestInterceptor}
            responseInterceptor={chatResponseInterceptor}
            chatStyle={{
              fontFamily: 'Source Sans Pro, sans-serif',
              width: '380px',
              height: '520px',
              borderEndEndRadius: '10px',
              borderEndStartRadius: '10px',
            }}
            messageStyles={{
              default: {
                shared: {
                  bubble: { width: 'fit-content', maxWidth: '80%' },
                },
                user: {
                  bubble: {
                    backgroundColor: '#f16347',
                    marginRight: '-40px',
                    paddingRight: '40px',
                  },
                },
                ai: {
                  bubble: {
                    backgroundColor: '#f5f7fb',
                    marginLeft: '-40px',
                    paddingLeft: '40px',
                  },
                },
              },
              loading: {
                bubble: {
                  color: '#f16347',
                  backgroundColor: '#f5f7fb',
                  marginLeft: '-40px',
                  paddingLeft: '50px',
                  height: '13px',
                  width: '20px',
                },
              },
            }}
            avatars={{
              ai: {
                src: '/images/chat-assistant-avatar.png',
                styles: { container: { zIndex: '999' } },
              },
              user: { src: '/images/user-avatar.png' },
            }}
          />
        </div>
      )}
      <div className='fixed bottom-4 right-4'>
        <Tooltip
          content={t(`tooltip.${chatStatus}-assistant`)}
          alignment='start'
          placement='top'
          open={!isChatOpen || (!isChatReady && isChatOpen)}
        >
          <button
            ref={chatButtonRef}
            className={cn(
              'inline-flex items-center justify-center',
              'text-sm font-medium rounded-full w-16 h-16 bg-vibrant-orange',
              'disabled:pointer-events-none disabled:opacity-50',
              'cursor-pointer',
              isChatReady && 'hover:opacity-80',
            )}
            type='button'
            aria-haspopup='dialog'
            aria-expanded='false'
            onClick={toggleChat}
            disabled={!isChatReady}
          >
            {isChatSyncing ? <InlineLoader /> : <Chat />}
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

export default OfferDetails;
